<template>
    <v-card class="round-card background-2 mb-4">
        <v-card-title class="pa-3">
            <v-icon color="error" class="mr-3">mdi-check-circle</v-icon>
            <p>Chiudi Ticket</p>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text>
            <div v-if="loading" class="text-center py-4">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <div v-else>
                <!-- Toggle Invio Email -->
                <v-switch
                    v-model="sendEmail"
                    label="Invia email al cliente"
                    color="primary"
                    class="mt-0 px-6"
                    inset
                    :disabled="!closeTemplate"
                ></v-switch>

                <!-- Alert template mancante -->
                <v-alert
                    v-if="!closeTemplate"
                    type="warning"
                    dense
                    outlined
                    class="mb-3 mx-6"
                >
                    Nessun template configurato per l'email di chiusura.
                    Configura un template nelle impostazioni Helpdesk.
                </v-alert>

                <!-- Info template (se configurato) -->
                <v-alert
                    v-else-if="sendEmail"
                    type="info"
                    dense
                    outlined
                    class="mb-3 mx-6"
                >
                    <v-icon left small>mdi-file-document-outline</v-icon>
                    Email con template: <strong>{{ closeTemplate.name }}</strong>
                </v-alert>

                <!-- Sezione Email (visibile solo se sendEmail) -->
                <v-expand-transition>
                    <div v-if="sendEmail && closeTemplate" class="px-6">
                        <!-- Destinatario -->
                        <v-combobox
                            v-model="emailForm.to"
                            :items="emailsTo"
                            label="A"
                            v-validate="sendEmail ? 'email|required' : ''"
                            :data-vv-name="'A'"
                            :error-messages="errors.collect('A')"
                            outlined
                            dense
                            class="mb-0"
                            :return-object="false"
                        >
                            <template v-slot:item="data">
                                <v-list-item-avatar size="32px" class="primary mr-4">
                                    <v-icon color="white" small>mdi-email</v-icon>
                                </v-list-item-avatar>
                                <v-list-item-content>
                                    <v-list-item-title>{{ data.item.text || data.item }}</v-list-item-title>
                                </v-list-item-content>
                            </template>
                        </v-combobox>

                        <!-- CC -->
                        <v-combobox
                            v-model="emailForm.cc"
                            label="CC (opzionale)"
                            multiple
                            chips
                            small-chips
                            deletable-chips
                            outlined
                            dense
                            class="mb-3"
                            hint="Premi invio per aggiungere un indirizzo"
                            persistent-hint
                        >
                            <template v-slot:selection="{ attrs, item, parent, selected }">
                                <v-chip
                                    v-bind="attrs"
                                    :input-value="selected"
                                    small
                                    close
                                    @click:close="parent.selectItem(item)"
                                >
                                    {{ item }}
                                </v-chip>
                            </template>
                        </v-combobox>

                        <!-- Corpo Email -->
                        <div class="mb-3">
                            <label class="v-label mb-2 d-block">Messaggio</label>
                            <vue-editor
                                id="close-mail-text"
                                v-model="emailForm.body"
                            ></vue-editor>
                        </div>

                        <!-- Checkbox allega PDF -->
                        <v-checkbox
                            v-model="attachPdf"
                            label="Allega PDF riepilogo ticket"
                            color="primary"
                            class="mt-0"
                        ></v-checkbox>
                    </div>
                </v-expand-transition>

                <v-divider class="my-4"></v-divider>

                <!-- Anteprima PDF -->
                <div class="px-6">
                    <v-card outlined class="mb-3">
                        <v-card-title class="py-2">
                            <v-icon left color="red">mdi-file-pdf-box</v-icon>
                            <span class="subtitle-1">Riepilogo Ticket (PDF)</span>
                            <v-spacer></v-spacer>
                            <pdf-preview-dialog
                                :button="pdfPreviewButton"
                                :id="ticketId"
                            ></pdf-preview-dialog>
                        </v-card-title>
                    </v-card>
                </div>
            </div>
        </v-card-text>

        <!-- Allegati -->
        <v-expand-transition>
            <div v-if="showAttachments && sendEmail && fileUploaderInfo.attachableType" class="px-6 pb-4">
                <file-uploader
                    class="pa-0"
                    :id="ticketId"
                    :attachable-type="fileUploaderInfo.attachableType"
                    :options="fileUploaderInfo.options || {}"
                    :field="fileUploaderInfo"
                    :start-empty="true"
                ></file-uploader>
            </div>
        </v-expand-transition>

        <v-divider></v-divider>

        <v-card-actions class="pa-3">
            <v-btn
                v-if="sendEmail"
                text
                @click="showAttachments = !showAttachments"
                :color="showAttachments ? 'primary' : 'grey'"
            >
                <v-icon left>mdi-paperclip</v-icon>
                Allegati
                <v-icon right small>{{ showAttachments ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn text @click="$emit('cancel')" :disabled="closing">
                Annulla
            </v-btn>
            <v-btn
                color="error"
                @click="closeTicket"
                :loading="closing"
            >
                <v-icon left>mdi-check-circle</v-icon>
                {{ sendEmail ? 'Chiudi e Invia' : 'Chiudi Ticket' }}
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import FileUploader from '@/components/common/FileUploader.vue'
import PdfPreviewDialog from '@/components/common/PdfPreviewDialog.vue'

export default {
    name: 'CloseTicketPanel',

    components: {
        FileUploader,
        PdfPreviewDialog
    },

    props: {
        ticketId: {
            type: Number,
            required: true
        }
    },

    data() {
        return {
            loading: false,
            closing: false,
            sendEmail: true,
            emailForm: {
                to: '',
                cc: [],
                body: ''
            },
            emailsTo: [],
            fileUploaderInfo: {},
            attachmentIds: [],
            showAttachments: false,
            attachPdf: true,
            closeTemplate: null
        }
    },

    computed: {
        pdfPreviewButton() {
            return {
                params: {
                    url: 'helpdesktroubles',
                    icon: 'mdi-eye',
                    tooltip: 'Anteprima PDF'
                }
            }
        }
    },

    methods: {
        async loadData() {
            this.loading = true
            try {
                // Carica info email (destinatari, file uploader, template chiusura)
                const mailInfoResponse = await this.axios.get(`helpdesktroubles/${this.ticketId}/send-mail-info`)
                const mailData = mailInfoResponse.data.data

                this.emailsTo = mailData.emails_to || []
                this.emailForm.to = mailData.default_to || ''
                this.fileUploaderInfo = mailData.file_uploader_info || {}

                // Template chiusura direttamente dal response (già dall'account corretto)
                if (mailData.close_template) {
                    // Carica e parsa il template
                    const templateResponse = await this.axios.get(`helpdesktroubles/${this.ticketId}/parse-template/${mailData.close_template.id}`)
                    this.closeTemplate = templateResponse.data.data
                    this.emailForm.body = this.closeTemplate.body || ''
                } else {
                    this.closeTemplate = null
                    this.sendEmail = false
                }
            } catch (error) {
                console.error('Error loading close panel data:', error)
            } finally {
                this.loading = false
            }
        },

        async closeTicket() {
            // Validazione
            if (this.sendEmail) {
                const valid = await this.$validator.validateAll()
                if (!valid) return
            }

            this.closing = true
            try {
                const payload = {
                    send_email: this.sendEmail
                }

                if (this.sendEmail) {
                    payload.email_to = this.emailForm.to
                    payload.email_cc = this.emailForm.cc
                    payload.email_body = this.emailForm.body
                    payload.attachment_ids = this.attachmentIds
                    payload.attach_pdf = this.attachPdf
                }

                await this.axios.post(`helpdesktroubles-close/${this.ticketId}`, payload)

                this.$store.commit('setSnackbar', {
                    value: true,
                    text: this.sendEmail ? 'Ticket chiuso e email inviata' : 'Ticket chiuso',
                    color: 'success',
                    timeout: 3000
                })
                this.$emit('closed')
            } catch (error) {
                console.error('Error closing ticket:', error)
            } finally {
                this.closing = false
            }
        }
    },

    mounted() {
        this.$eventBusUtility.$on('paths', (files) => {
            this.attachmentIds = files
        })
        this.loadData()
    },

    beforeDestroy() {
        this.$eventBusUtility.$off('paths')
    }
}
</script>
